import type { Translations } from '../types';

export const en: Translations = {
    common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        add: 'Add',
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
        search: 'Search',
        noResults: 'No results',
        refresh: 'Refresh',
        logout: 'Logout',
        settings: 'Settings',
        download: 'Download',
    },
    nav: {
        chat: 'Chat',
        gallery: 'Gallery',
        settings: 'Settings',
        admin: 'Admin',
        users: 'Users',
        models: 'Models',
    },
    auth: {
        login: 'Login',
        register: 'Register',
        username: 'Username',
        password: 'Password',
        currentPassword: 'Current Password',
        newPassword: 'New Password',
        confirmPassword: 'Confirm Password',
        loginButton: 'Login',
        registerButton: 'Register',
        loggingIn: 'Logging in...',
        invalidCredentials: 'Invalid username or password',
        changePassword: 'Change Password',
        changingPassword: 'Changing...',
        passwordChanged: 'Password changed. Other sessions have been logged out.',
        invalidateAllSessions: 'Logout All Devices',
        allSessionsInvalidated: 'All sessions invalidated. Please login again.',
    },
    chat: {
        newChat: 'New Chat',
        sendMessage: 'Send',
        typeMessage: 'Type a message...',
        noChats: 'No chats',
        startNewChat: 'Start a new chat',
        deleteChat: 'Delete Chat',
        deleteChatConfirm: 'Delete this chat?',
        deleteMessages: 'Delete Messages',
        deleteMessageConfirm: 'Delete this message?',
        exportChat: 'Export Chat',
        importChat: 'Import Chat',
        selectModel: 'Select Model',
        thinking: 'Thinking...',
    },
    gallery: {
        title: 'Gallery',
        description: 'Manage generated images and videos',
        upload: 'Upload',
        dragDrop: 'Drag or click to upload',
        delete: 'Delete',
        move: 'Move',
        sync: 'Sync',
        noFiles: 'No files',
        syncComplete: 'Sync complete',
        uploadComplete: 'Upload complete',
        deleteConfirm: 'Delete selected files?',
        newFolder: 'New Folder',
        // Metadata viewer
        path: 'Path',
        filename: 'Filename',
        no_metadata: 'No metadata available',
        model: 'Model',
        seed: 'Seed',
        size: 'Size',
        sampler: 'Sampler',
        prompt: 'Prompt',
        negative_prompt: 'Negative Prompt',
        ai_analyze: 'AI Chat Analysis',
        mode_image: 'Image only',
        mode_prompt: 'Prompt only',
        mode_both: 'Image + Prompt',
        apply_settings: 'Apply to Generator',
    },
    settings: {
        title: 'Settings',
        apiKey: 'Google API Key',
        apiKeyDesc: 'API key for using Gemini API',
        apiKeyPlaceholder: 'API key starting with AIza...',
        apiKeySaved: 'API key saved.',
        apiKeyRemoved: 'API key removed.',
        removeApiKey: 'Remove API Key',
        r2Config: 'R2 Storage',
        r2ConfigDesc: 'Cloudflare R2 storage configuration',
        r2Configured: 'R2 is configured',
        r2SaveSuccess: 'R2 settings saved.',
        r2ChangeHint: 'To change R2 settings, fill out the form below again.',
        autoSave: 'Auto-save Media',
        autoSaveDesc: 'Automatically save generated images/videos to R2',
        chatBackup: 'Chat Backup',
        exportAll: 'Export All',
        importAll: 'Import All',
        backupDesc: 'Backup or restore all chats and messages as a JSON file.',
        backupSuccess: 'Backup complete',
        backupFailed: 'Backup failed',
        importSuccess: 'Import complete',
        importFailed: 'Import failed',
        security: 'Security',
        securityDesc: 'Password change and session management',
        passwordChangeHint: 'Changing password will log out other devices.',
        sessionLogoutHint: 'Log out from all other devices.',
        systemPrompts: 'System Prompts',
        addPrompt: 'Add',
        promptTitle: 'Title',
        promptContent: 'Content',
        deletePromptConfirm: 'Delete this prompt?',
        clearCache: 'Clear Cache',
        clearCacheDesc: 'Reset browser UI state',
        clearCacheConfirm: 'Clear local cache? UI state will be reset.',
        cacheClearedRefresh: 'Cache cleared. Refreshing page.',
    },
    admin: {
        users: {
            title: 'User Management',
            description: 'Create and manage user accounts',
            addUser: 'Add User',
            username: 'Username',
            password: 'Password',
            role: 'Role',
            admin: 'Admin',
            user: 'User',
            createdAt: 'Created At',
            actions: 'Actions',
            noUsers: 'No users registered',
            deleteConfirm: 'Delete this user?',
            createFailed: 'Failed to create user.',
            cannotDeleteSelf: 'Cannot delete yourself.',
            cannotChangeOwnRole: 'Cannot change your own role.',
            passwordMinLength: 'Password must be at least 8 characters.',
            passwordChanged: 'Password has been changed.',
            resetPassword: 'Reset Password',
            newPassword: 'New password (8+ chars)',
            enterNewPassword: '\'s new password:',
        },
        models: {
            title: 'Model Settings',
            description: 'Manage AI models available to users',
            fetchFromGoogle: 'Fetch from Google',
            fetchModels: 'Fetch Models',
            noModels: 'No models registered',
            noModelsDesc: 'Click "Fetch from Google" to load available models.',
            active: 'Active',
            inactive: 'Inactive',
            modelsFetched: 'Models fetched',
            fetchFailed: 'Failed to fetch models. Check your API key.',
            deleteConfirm: 'Delete this model?',
            helpTitle: 'Help',
            helpItems: [
                'Only active models are shown to users.',
                '"Fetch from Google" uses the API key saved in Settings.',
                'Model list is provided by Google Gemini API.',
            ],
        },
    },
    time: {
        justNow: 'just now',
        minutesAgo: ' min ago',
        hoursAgo: ' hours ago',
        daysAgo: ' days ago',
    },
    errors: {
        networkError: 'Network error occurred',
        unauthorized: 'Authentication required',
        serverError: 'Server error occurred',
        notFound: 'Not found',
        rateLimited: 'Too many requests. Please try again later.',
    },
};
